import { useEffect } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useAgentRuntime, useAgentStore } from '../context/AgentProvider'

export const DEFAULT_MESSAGES_PAGE_SIZE = 20

interface UseMessagesInfiniteOptions {
  /**
   * By default messages are only fetched for chats registered in the store's
   * chat list (guards against fetching for local draft keys). Subagent chats
   * are real server chats that never appear in that list, so their previews
   * opt out of the check.
   */
  requireRegisteredChat?: boolean
  /**
   * Set false to defer fetching (e.g. a collapsed preview that hasn't been
   * opened yet). Already-fetched data stays available. Defaults to true.
   */
  enabled?: boolean
}

export const useMessagesInfinite = (chatId: string | undefined, options?: UseMessagesInfiniteOptions) => {
  const requireRegisteredChat = options?.requireRegisteredChat ?? true
  const runtime = useAgentRuntime()
  const pageSize = runtime.config.messagesPageSize ?? DEFAULT_MESSAGES_PAGE_SIZE
  const setChatMessages = useAgentStore((state) => state.setChatMessages)
  const chatExists = useAgentStore((state) => Boolean(chatId && state.chats[chatId]))

  const query = useInfiniteQuery({
    queryKey: ['mp-agents', 'messages', chatId, pageSize],
    queryFn: async ({ pageParam }) => {
      if (!chatId || !runtime.transport.getMessages) throw new Error('No chat or transport')
      const result = await runtime.transport.getMessages(chatId, {
        limit: pageSize,
        ...(pageParam ? { before: pageParam } : {})
      })
      return result
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore && lastPage.messages.length > 0 ? lastPage.messages[0]?.id : undefined,
    enabled:
      (options?.enabled ?? true) &&
      Boolean(chatId) &&
      Boolean(runtime.transport.getMessages) &&
      (!requireRegisteredChat || chatExists),
    retry: false
  })

  useEffect(() => {
    if (!chatId || !query.data?.pages) return

    const pages = query.data.pages
    const paginatedMessages = pages
      .slice()
      .reverse()
      .flatMap((p) => p.messages)
    const paginatedIds = new Set(paginatedMessages.map((m) => m.id))
    setChatMessages(chatId, (prev) => {
      const realtimeOnly = prev.filter((m) => !paginatedIds.has(m.id))
      return [...paginatedMessages, ...realtimeOnly]
    })
  }, [chatId, query.data, setChatMessages])

  return query
}
